'use client';

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '@/components/i18n-provider';
import { X, AlertCircle, Check, ArrowRight, Wallet } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { AddCreditsDialog } from '@/components/add-credits-dialog';
import { useAnalytics } from '@/hooks/use-analytics';
import { displayPlanName } from '@/lib/plans/display';
import { cn } from '@/lib/utils';

interface PlanInfo {
  id: string;
  name: string;
  priceId: string | null;
  type: 'subscription';
  price: number;       // cents
  credits: string;
  features: string[];
  isFree?: boolean;
  isPopular?: boolean;
}

interface BalanceSummary {
  hasStripeCustomer: boolean;
  planName: string | null;
  subscriptionStatus: string | null;
  availableCredits: number;
}

interface SubscribeDialogProps {
  open: boolean;
  onClose: () => void;
}

/* ------------------------------------------------------------------ */
/*  Plan card                                                          */
/* ------------------------------------------------------------------ */
type ButtonMode = 'current' | 'disabled' | 'downgrade' | 'upgrade';

function PlanCard({
  plan,
  isLoading,
  isSelectable,
  badgeText,
  buttonMode,
  hasActiveSub,
  justSwitched,
  onSelect,
  t,
}: {
  plan: PlanInfo;
  isLoading: boolean;
  isSelectable: boolean;
  badgeText: string | null;
  buttonMode: ButtonMode;
  hasActiveSub: boolean;
  justSwitched: boolean;
  onSelect: () => void;
  t: (key: string, params?: Record<string, string>) => string;
}) {
  const hasBadge = !!badgeText;

  return (
    <div
      className={`group select-none rounded-lg overflow-hidden border flex flex-col bg-card transition-all duration-500 ${
        justSwitched ? 'ring-2 ring-primary/50 border-primary bg-primary/5' :
        hasBadge ? 'shadow-lg border-primary' : 'border-border'
      } ${isSelectable ? 'hover:border-primary/60 hover:shadow-md hover:bg-accent/30 cursor-pointer' : ''}`}
      onClick={() => { if (isSelectable) onSelect(); }}
    >
      {hasBadge && (
        <div className="bg-primary text-primary-foreground text-center py-1.5 text-xs font-semibold tracking-wide uppercase">
          {badgeText}
        </div>
      )}
      <div className="p-4 flex-1 flex flex-col">
        <h3 className="text-base font-semibold">{plan.name}</h3>
        <div className="mt-1 mb-2">
          <span className="text-xl font-bold">
            ${(plan.price / 100).toFixed(0)}
          </span>
          {!plan.isFree && (
            <span className="text-xs text-muted-foreground">{t('subscribe.perMonth')}</span>
          )}
        </div>
        <p className={`text-xs font-medium mb-3 ${
          plan.isFree ? 'text-muted-foreground' : 'text-primary'
        }`}>
          {plan.isFree
            ? t('subscribe.credits', { credits: plan.credits })
            : t('subscribe.creditsPerMonth', { credits: plan.credits })}
        </p>

        {plan.features.length > 0 && (
          <ul className="space-y-1.5 mb-4 flex-1">
            {plan.features.map((feat, i) => (
              <li key={i} className="flex items-start text-xs gap-1.5">
                <Check className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${
                  hasBadge ? 'text-primary' : 'text-muted-foreground'
                }`} />
                <span className="text-muted-foreground">{feat}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-auto pt-2">
          {buttonMode === 'current' ? (
            <button
              type="button"
              disabled
              className="w-full py-2 px-3 rounded-md text-xs font-medium bg-muted text-muted-foreground"
            >
              {t('subscribe.currentPlan')}
            </button>
          ) : buttonMode === 'disabled' ? (
            <button
              type="button"
              disabled
              className="w-full py-2 px-3 rounded-md text-xs font-medium bg-muted text-muted-foreground/60"
            >
              {plan.isFree ? t('subscribe.free') : t('subscribe.comingSoon')}
            </button>
          ) : buttonMode === 'downgrade' ? (
            <button
              type="button"
              onClick={onSelect}
              disabled={isLoading}
              className="w-full py-2 px-3 rounded-md text-xs font-medium transition-colors flex items-center justify-center gap-1.5 bg-secondary text-secondary-foreground hover:bg-secondary/80 disabled:opacity-50"
            >
              {isLoading ? (
                <Spinner size="sm" />
              ) : (
                <>
                  {t('subscribe.switchPlan')}
                  <ArrowRight className="h-3.5 w-3.5" />
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={onSelect}
              disabled={isLoading}
              className="w-full py-2 px-3 rounded-md text-xs font-medium transition-colors flex items-center justify-center gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isLoading ? (
                <Spinner size="sm" />
              ) : (
                <>
                  {t(hasActiveSub ? 'subscribe.switchPlan' : 'subscribe.choosePlan')}
                  <ArrowRight className="h-3.5 w-3.5" />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main dialog                                                        */
/* ------------------------------------------------------------------ */
export function SubscribeDialog({ open, onClose }: SubscribeDialogProps) {
  const { t } = useTranslation();
  const { trackSubscribe, trackCheckoutStart } = useAnalytics();
  const [plans, setPlans] = useState<PlanInfo[]>([]);
  const [creditPriceId, setCreditPriceId] = useState<string | null>(null);
  const [balance, setBalance] = useState<BalanceSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setLoading(true);
      setError(null);
      Promise.all([
        fetch('/api/stripe/plans').then((r) => r.json()),
        fetch('/api/balance').then((r) => (r.ok ? r.json() : null)),
      ])
        .then(([plansData, balanceData]) => {
          setPlans((plansData.plans ?? []).filter((p: PlanInfo) => p.type === 'subscription'));
          setCreditPriceId(plansData.creditPriceId ?? null);
          if (balanceData) {
            setBalance({
              hasStripeCustomer: !!balanceData.hasStripeCustomer,
              planName: balanceData.planName ?? null,
              subscriptionStatus: balanceData.subscriptionStatus ?? null,
              availableCredits: balanceData.availableCredits ?? 0,
            });
          } else {
            setBalance(null);
          }
        })
        .catch(() => setBalance(null))
        .finally(() => setLoading(false));
    }
  }, [open]);

  const [switchedPlanId, setSwitchedPlanId] = useState<string | null>(null);
  const [confirmDowngrade, setConfirmDowngrade] = useState<PlanInfo | null>(null);
  // Secondary path: one-time top-up. Subscription is the default flow; the
  // top-up dialog opens from the always-visible footer button.
  const [showAddCredits, setShowAddCredits] = useState(false);

  const refreshBalance = async () => {
    try {
      const balanceData = await fetch('/api/balance').then((r) => (r.ok ? r.json() : null));
      if (balanceData) {
        setBalance({
          hasStripeCustomer: !!balanceData.hasStripeCustomer,
          planName: balanceData.planName ?? null,
          subscriptionStatus: balanceData.subscriptionStatus ?? null,
          availableCredits: balanceData.availableCredits ?? 0,
        });
      }
    } catch { /* best-effort */ }
  };

  const handleCheckout = async (priceId: string, mode: 'subscription' | 'payment', forceSwitch?: boolean) => {
    if (!priceId) return;
    setCheckoutLoading(priceId);
    setError(null);
    try {
      // `successUrl`/`cancelUrl` used to be sent here but the API's
      // `checkoutSchema` doesn't accept them — the route hardcodes the
      // return URLs (now `/workspace?wallet=1[&payment=success&session_id=…]`)
      // so passing them was dead code. Dropping the fields matches the
      // API contract.
      const body: Record<string, unknown> = {
        priceId,
        mode,
      };
      if (mode === 'payment') {
        body.quantity = 25;
      }
      if (forceSwitch) {
        body.forceSwitch = true;
      }
      const res = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (data.switched) {
        setSwitchedPlanId(priceId);
        trackSubscribe(priceId);
        await refreshBalance();
        setTimeout(() => setSwitchedPlanId(null), 3000);
        return;
      }
      if (data.url) {
        trackCheckoutStart(priceId);
        window.location.href = data.url;
        return;
      }
      // Human sentence first: `error` is a machine slug (`purchases_disabled`,
      // `purchases_unavailable_region`) and must never reach a person.
      setError(data.message ?? data.error ?? t('subscribe.checkoutFailed'));
    } catch (e) {
      console.error(e);
      setError(t('subscribe.somethingWentWrong'));
    } finally {
      setCheckoutLoading(null);
    }
  };

  const resetConfirm = useCallback(() => setConfirmDowngrade(null), []);

  // Clear confirmation when dialog closes
  useEffect(() => { if (!open) resetConfirm(); }, [open, resetConfirm]);

  // Close on Esc so the user can dismiss with the keyboard. The wallet
  // dialog chrome we're nested inside also handles Esc (via
  // FullscreenNavProvider), but it falls back to closing the parent
  // wallet when this dialog isn't visible — so we need to swallow Esc
  // here whenever we're open, and stop propagation so the parent
  // doesn't also close.
  useEffect(() => {
    // While the Add-Credits child dialog is stacked on top, ITS Esc handler
    // owns the key — ours must not also fire (both are capture-phase
    // document listeners, and ours registered first).
    if (!open || showAddCredits) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    }
    // Capture phase so we beat FullscreenNavProvider's bubble-phase
    // listener and prevent the parent wallet dialog from closing too.
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, showAddCredits, onClose]);

  if (!open) return null;

  const hasActiveSub = !!balance?.planName;

  // Determine current plan's price to detect upgrades vs downgrades.
  // Compare through displayPlanName: the live Stripe products still carry
  // legacy tier names (Starter / Plus / Pro) while the dialog displays the
  // post-pivot model (Free / Premium) — a raw name comparison marked a
  // Premium subscriber as not-current.
  const normalizedPlanName = displayPlanName(balance?.planName);
  const currentPlan = hasActiveSub
    ? plans.find((p) => normalizedPlanName?.toLowerCase() === p.name.toLowerCase())
    : null;
  const currentPrice = currentPlan?.price ?? 0;
  const currentTier = currentPlan?.id ?? '';

  // If the user is on Plus or higher, shift the upsell badge to Pro
  const highlightTier = currentTier === 'plus' || currentTier === 'pro' ? 'pro' : '';

  // Dialog chrome matches the workspace wallet / settings dialog
  // (FullscreenNavProvider): translucent `bg-black/40 backdrop-blur-sm`
  // overlay, click-outside to close, `glass-panel rounded-2xl` panel
  // capped at 1200×92vh, and the same compact header (title + X with
  // an inset border underneath). The previous bare-fullscreen treatment
  // felt like a completely different surface from the wallet dialog
  // that opened it — this keeps the visual language consistent.
  //
  // z-[210] so we stack above the parent wallet dialog (z-[200] in
  // FullscreenNavProvider) but below critical-toasts (z-[300]).
  //
  // CRITICAL: portal to document.body. This dialog is rendered from
  // chat-view (and balance-card / wallet-view), and chat-view lives
  // inside an agent widget on the workspace canvas. The canvas uses
  // CSS `transform` for pan/zoom — which establishes a containing
  // block for *fixed*-positioned descendants — so without the portal,
  // `fixed inset-0` would clip to the widget bounds instead of the
  // viewport (you'd see the plan grid stuffed inside a tiny widget).
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="subscribe-dialog-title"
      className="fixed inset-0 z-[210] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={cn(
          'glass-panel relative flex flex-col overflow-hidden rounded-2xl shadow-2xl',
          // Content-sized: the plan grid is ~960px wide, so cap there
          // and let height grow to fit the cards instead of forcing
          // `h-[92vh]` and leaving a vast empty band below them. The
          // body still has its own `overflow-y-auto` so taller content
          // (e.g. error banner + 5+ plans on a future SKU change)
          // scrolls inside the panel up to the 92vh ceiling.
          'h-auto max-h-[92vh] w-[min(960px,96vw)]',
        )}
      >
        {/* Header — matches the wallet dialog: compact title + close,
            no border-bottom, just an inset shadow hairline. */}
        <div className="flex shrink-0 items-center gap-2 px-4 py-3 shadow-[inset_0_-1px_0_rgba(15,23,42,0.06)] dark:shadow-[inset_0_-1px_0_rgba(0,0,0,0.35)]">
          <h2
            id="subscribe-dialog-title"
            className="min-w-0 flex-1 truncate text-sm font-semibold tracking-tight text-foreground"
          >
            {t('subscribe.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('workspace.canvas.node.close')}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body — single centered max-width so the plan grid lays out
            consistently on ultra-wide panels without becoming a wall
            of cards stretched edge-to-edge. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {/* No inner max-width — the outer panel is already capped
              at `min(960px, 96vw)`, and the plan grid (4 columns)
              fills that width comfortably. An additional inner cap
              just left visible side-padding. */}
          <div className="mx-auto w-full">
        {/* Error banner */}
        {error && (
          <div className="flex items-center gap-2 mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Content — one 2-column rhythm for the whole dialog: the two
            purchasable plans (Free · Premium) fill the row, and the
            Team / Enterprise teasers below align to the same columns.
            The old lg:grid-cols-4 left the two plan cards squeezed into
            the left half with a dead right half, while the full-width
            teaser row below made the misalignment obvious. */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner size="2xl" tone="muted" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {plans.map((plan) => {
              const isCurrent =
                (plan.isFree && !balance?.planName) ||
                (!!normalizedPlanName &&
                  normalizedPlanName.toLowerCase() === plan.name.toLowerCase());

              const isDowngrade = hasActiveSub && !isCurrent && plan.price < currentPrice;
              const isSelectable = !isCurrent && !!plan.priceId;

              let badgeText: string | null = null;
              if (highlightTier && plan.id === highlightTier) {
                badgeText = t('subscribe.mostPowerful');
              } else if (!highlightTier && plan.isPopular) {
                badgeText = t('subscribe.mostPopular');
              }

              let buttonMode: ButtonMode;
              if (isCurrent) {
                buttonMode = 'current';
              } else if (!isSelectable) {
                buttonMode = 'disabled';
              } else if (isDowngrade) {
                buttonMode = 'downgrade';
              } else {
                buttonMode = 'upgrade';
              }

              // Inline confirmation replaces the target card
              if (confirmDowngrade?.id === plan.id && currentPlan) {
                const isConfirming = checkoutLoading === plan.priceId;
                return (
                  <div
                    key={plan.id}
                    className="select-none rounded-lg overflow-hidden border border-yellow-500/40 flex flex-col bg-yellow-500/5"
                  >
                    <div className="bg-yellow-600 text-white text-center py-1.5 text-xs font-semibold tracking-wide uppercase">
                      {t('subscribe.confirmDowngradeTitle', {
                        from: currentPlan.name,
                        to: plan.name,
                      })}
                    </div>
                    <div className="p-4 flex-1 flex flex-col">
                      <p className="text-xs text-muted-foreground mb-2">
                        {t('subscribe.confirmDowngradeDesc')}
                      </p>
                      {currentPlan.features.length > 0 && (
                        <ul className="space-y-1 mb-3 flex-1">
                          {currentPlan.features.map((feat, i) => (
                            <li key={i} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                              <X className="h-3.5 w-3.5 mt-0.5 shrink-0 text-yellow-600" />
                              <span>{feat}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="mt-auto pt-2 space-y-1.5">
                        <button
                          type="button"
                          onClick={() => {
                            if (plan.priceId) {
                              handleCheckout(plan.priceId, 'subscription', true);
                            }
                            resetConfirm();
                          }}
                          disabled={isConfirming}
                          className="w-full py-2 px-3 rounded-md text-xs font-medium bg-yellow-600 text-white hover:bg-yellow-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
                        >
                          {isConfirming ? <Spinner size="sm" /> : null}
                          {t('subscribe.confirmDowngradeAction')}
                        </button>
                        <button
                          type="button"
                          onClick={resetConfirm}
                          className="w-full py-2 px-3 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                        >
                          {t('subscribe.confirmDowngradeCancel')}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  badgeText={badgeText}
                  buttonMode={buttonMode}
                  hasActiveSub={hasActiveSub}
                  // `!= null` guard: the Free plan has no priceId, and the
                  // initial `switchedPlanId` is null too — a bare `===`
                  // matched null===null and permanently painted the Free
                  // card with the "just switched" celebration ring.
                  justSwitched={plan.priceId != null && switchedPlanId === plan.priceId}
                  isSelectable={isSelectable}
                  isLoading={checkoutLoading === plan.priceId && checkoutLoading !== null}
                  onSelect={() => {
                    if (!plan.priceId) return;
                    if (isDowngrade) {
                      setConfirmDowngrade(plan);
                    } else {
                      handleCheckout(plan.priceId, 'subscription');
                    }
                  }}
                  t={t}
                />
              );
            })}
          </div>
        )}

        {/* Roadmap teasers — Team and Enterprise are not yet purchasable.
            Same 2-column grid as the plans so the rows align. */}
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {[
            { name: 'Team', blurb: 'Shared spaces, per-doc collaborators, team billing and admin.' },
            { name: 'Enterprise', blurb: 'SSO and advanced security, dedicated support, custom limits.' },
          ].map((tier) => (
            <div
              key={tier.name}
              className="rounded-lg border border-border p-3 opacity-70"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{tier.name}</span>
                <span className="rounded-md bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                  {t('subscribe.comingSoon')}
                </span>
              </div>
              <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{tier.blurb}</p>
            </div>
          ))}
        </div>

        {/* Footer — the SECONDARY path: a one-time top-up, always visible
            (it used to be a subscribers-only footnote that went straight to
            checkout). Subscription stays the default flow above. */}
        {creditPriceId && (
          <div className="mt-4 flex items-center justify-center text-xs text-muted-foreground">
            <button
              type="button"
              onClick={() => setShowAddCredits(true)}
              className="hover:text-foreground flex items-center gap-1 transition-colors"
            >
              <Wallet className="h-3 w-3" />
              {t('subscribe.oneTimeTopUpHint')}
            </button>
          </div>
        )}
          </div>
        </div>
      </div>
      <AddCreditsDialog open={showAddCredits} onClose={() => setShowAddCredits(false)} />
    </div>,
    document.body,
  );
}
